/* global Razorpay */
import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  Modal,
  Tag,
  Skeleton,
  Spin,
  message,
} from "antd";
import { useSelector } from "react-redux";
import { fetchCustomers } from "../customer/FetchCustomers";
import { fetchOrders } from "./FetchOrders";
import OrderItems from "../orderItems/OrderItems";
import CustomerAddress from "../coustomerAddress/CoustomerAddress";
import moment from "moment";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import {
  fetchAllOrders,
  getCustomerDetails,
  createOrder,
  cancelOrder,
  changeLabStatus,
  getOrderItemsByCustomer,
} from "../../endpoints/API";

const formatDateTime = (datetime) => {
  const dateObj = new Date(datetime);
  const formattedDate = dateObj.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const formattedTime = dateObj.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return { formattedDate, formattedTime };
};

const RAZORPAY_KEY = "rzp_test_nDqmupHpKKwbNT";

const Order = () => {
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const user = useSelector((state) => state.user.user);

  const [customers, setCustomers] = useState([]);
  const [customerData, setCustomerData] = useState(null);
  const [selectedCustomerProfile, setSelectedCustomerProfile] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Modal visibility states
  const [isOrderItemModalVisible, setIsOrderItemModalVisible] = useState(false);
  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false);
  const [isViewOrderItemsModalVisible, setIsViewOrderItemsModalVisible] = useState(false);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [isStatusChangeModalVisible, setIsStatusChangeModalVisible] = useState(false);

  // Order item view state
  const [selectedOrderItems, setSelectedOrderItems] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isOrderNumber, setIsOrderNumber] = useState(null);

  // Cancel & status change state
  const [cancellationReason, setCancellationReason] = useState("");
  const [currentStatus, setCurrentStatus] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");

  // ─── Data Fetching ────────────────────────────────────────────────────────────

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        // fetchCustomers(setCustomers, baseUrl, prodTestUrl),
        fetchOrders(setOrders, 1, ""),
      ]);
    } catch {
      message.error("Failed to fetch data.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (!selectedCustomerProfile) return;

    const loadCustomerData = async () => {
      try {
        const [itemsRes, detailsRes] = await Promise.all([
          getOrderItemsByCustomer(selectedCustomerProfile),
          getCustomerDetails(selectedCustomerProfile),
        ]);
        setOrderItems(itemsRes.data);
        setCustomerData(detailsRes.data);
      } catch {
        message.error("Failed to fetch customer data.");
      }
    };

    loadCustomerData();
  }, [selectedCustomerProfile]);

  // ─── Handlers ─────────────────────────────────────────────────────────────────

  const handleCancelOrder = async () => {
    if (!cancellationReason.trim()) {
      message.error("Cancellation reason is required.");
      return;
    }
    try {
      await cancelOrder(selectedOrderId, cancellationReason);
      message.success("Order cancelled successfully.");
      setIsCancelModalVisible(false);
      setCancellationReason("");
      setSelectedOrderId(null);
      await fetchOrders(setOrders, page, searchText);
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to cancel the order.");
    }
  };

  const handleStatusChange = async () => {
    if (!currentStatus) {
      message.error("Please select a status before submitting.");
      return;
    }
    try {
      await changeLabStatus(selectedOrderId, currentStatus);
      message.success("Status updated successfully!");
      setIsStatusChangeModalVisible(false);
      setCurrentStatus(null);
      await fetchOrders(setOrders, page, searchText);
    } catch {
      message.error("Failed to update status. Please try again.");
    }
  };

  const handleCreateOrder = async (values) => {
    const payload = {
      customer: values.customer,
      orderItems: values.orderItems,
      point_of_contact: values.point_of_contact,
      schedule_datetime: values.scheduleOrder.format("YYYY-MM-DD HH:mm:ss"),
      location: values.location,
      amount: values.amount,
    };

    try {
      const { data: orderData } = await createOrder(payload);

      const customerName = [
        orderData?.order?.customer?.first_name,
        orderData?.order?.customer?.middle_name,
        orderData?.order?.customer?.last_name,
      ]
        .filter(Boolean)
        .join(" ");

      const razorpayOptions = {
        key: RAZORPAY_KEY,
        amount: payload.amount * 100,
        currency: "INR",
        name: customerName,
        description: "Order Payment",
        order_id: orderData.order_number,
        notes: { address: orderData.location || "N/A" },
        theme: { color: "#3399cc" },
        handler: async (response) => {
          message.success(`Payment Successful! Payment ID: ${response.razorpay_payment_id}`);
          await fetchOrders(setOrders, page, searchText);
        },
      };

      const rzp = new Razorpay(razorpayOptions);
      rzp.on("payment.failed", () => message.error("Payment failed. Please try again."));
      rzp.open();
    } catch (err) {
      message.error(err?.response?.data?.message || "Something went wrong. Please try again.");
    }
  };

  const handleViewOrderItems = (id, orderNumber, items) => {
    setSelectedOrderId(id);
    setIsOrderNumber(orderNumber);
    setSelectedOrderItems(items);
    setIsViewOrderItemsModalVisible(true);
  };

  // ─── Table Columns ────────────────────────────────────────────────────────────

  const orderItemColumns = [
    {
      title: "Patient ID",
      dataIndex: "patientid",
      key: "patientid",
      render: (text) => text || "-",
    },
    {
      title: "Patient Name",
      dataIndex: "familyMemberName",
      key: "familyMemberName",
      render: (text) => text || "-",
    },
    {
      title: "Test Name (MRP)",
      dataIndex: "testNameWithMrp",
      key: "testNameWithMrp",
      render: (text) => text || "-",
    },
    {
      title: "Package Name (MRP)",
      dataIndex: "packageNameWithMrp",
      key: "packageNameWithMrp",
      render: (text) => text || "-",
    },
    {
      title: "Pouch",
      dataIndex: "pouch",
      key: "pouch",
    },
    {
      title: "Pouch Item",
      dataIndex: "pouch_item",
      key: "pouch_item",
      render: (pouchId) =>
        orders?.pouch_items
          ?.filter((item) => item?.pouch_id === pouchId)
          ?.map((item) =>
            item?.lab_user_scan_time ? (
              <Tag color="orange" key={item.barcode}>{item?.barcode}</Tag>
            ) : (
              <Skeleton.Input key={item.barcode} style={{ width: 50, marginTop: 5 }} active />
            )
          ),
    },
    {
      title: "Status",
      dataIndex: "patientid",
      key: "accession_status",
      render: (patientid) =>
        orders?.accession_items
          ?.filter((item) => item?.order_patient_id === patientid)
          ?.map((item, idx) => {
            if (item?.status === "partial")
              return <Tag color="green" key={idx}>Partial Accession Started</Tag>;
            if (item?.status === "complete")
              return <Tag color="green" key={idx}>Accession Started</Tag>;
            return null;
          }),
    },
  ];

  const orderItemDataSource = selectedOrderItems?.map((item, index) => ({
    key: index,
    patientid: `${isOrderNumber}-${item.familymember.id}`,
    familyMemberName: [
      item.familymember.first_name,
      item.familymember.middle_name,
      item.familymember.last_name,
    ]
      .filter(Boolean)
      .join(" "),
    testNameWithMrp: item?.test ? `${item.test.name} (₹${item.test.mrp})` : null,
    packageNameWithMrp: item?.package
      ? `${item.package.name_of_package} (₹${item.package.mrp})`
      : null,
    pouch: <Tag color="blue">{item?.pouch?.pouch_barcode}</Tag>,
    pouch_item: item?.pouch?.id,
  }));

  const mainTableColumns = [
    {
      title: "Order Number",
      dataIndex: "order_number",
      key: "order_number",
      render: (orderNumber) => <Tag>{orderNumber}</Tag>,
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      render: (customer) =>
        [customer.first_name, customer.middle_name, customer.last_name]
          .filter(Boolean)
          .join(" "),
    },
    {
      title: "Status",
      dataIndex: "order_status",
      key: "order_status",
      render: (status) => <Tag>{status}</Tag>,
    },
    {
      title: "Schedule Date-Time",
      dataIndex: "schedule_datetime",
      key: "schedule_datetime",
      render: (scheduleDateTime, record) => {
        const dt = scheduleDateTime || record.place_datetime;
        const { formattedDate, formattedTime } = formatDateTime(dt);
        return (
          <>
            <Tag>{formattedDate}</Tag>
            <Tag>{formattedTime}</Tag>
          </>
        );
      },
    },
    {
      title: "Order Items",
      key: "order_item",
      render: (_, record) => (
        <Button onClick={() => handleViewOrderItems(record.id, record.order_number, record.order_item)}>
          View Order Items
        </Button>
      ),
    },
  ];


  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <Spin spinning={isLoading}>
      <div>
        <Input.Search
          placeholder="Search by order number, customer name, phone"
          allowClear
          enterButton
          style={{ width: 300, marginBottom: 20 }}
          onSearch={(value) => {
            setPage(1);
            setSearchText(value);
            fetchOrders(setOrders, 1, value);
          }}
        />
        <Table
          dataSource={orders?.results}
          columns={mainTableColumns}
          rowKey="id"
          pagination={{
            current: page,
            pageSize: 10,
            total: orders?.count,
            showSizeChanger: false,   // remove page size dropdown
            showQuickJumper: true,    // enable "Go to page"
            onChange: (newPage) => {
              setPage(newPage);
              fetchOrders(setOrders, newPage, searchText);
              window.scrollTo({ top: 0, behavior: "smooth" }); // optional UX improvement
            },
          }}
        />

        {/* Create Order Item Modal */}
        <Modal
          title="Create Order Item"
          visible={isOrderItemModalVisible}
          onCancel={() => setIsOrderItemModalVisible(false)}
          footer={null}
        >
          <OrderItems
            customerData={customerData}
            setIsModalVisible={setIsOrderItemModalVisible}
            fetchCustomerData={() => getOrderItemsByCustomer(selectedCustomerProfile).then((r) => setOrderItems(r.data))}
          />
        </Modal>

        {/* Create Address Modal */}
        <Modal
          title="Create Address"
          visible={isAddressModalVisible}
          onCancel={() => setIsAddressModalVisible(false)}
          footer={null}
        >
          <CustomerAddress
            handleAddressModalCancel={() => setIsAddressModalVisible(false)}
            handleAdressModalOk={() => setIsAddressModalVisible(false)}
            selectedCustomerProfile={selectedCustomerProfile}
          />
        </Modal>

        {/* View Order Items Modal */}
        <Modal
          title="Order Items"
          visible={isViewOrderItemsModalVisible}
          onCancel={() => {
            setIsViewOrderItemsModalVisible(false);
            setSelectedOrderItems([]);
          }}
          footer={null}
          width={1000}
        >
          <Table
            columns={orderItemColumns}
            dataSource={orderItemDataSource}
            pagination={{ pageSize: 5 }}
            locale={{ emptyText: "No order items available for this order." }}
          />
        </Modal>

        {/* Cancel Order Modal */}
        <Modal
          title="Cancel Order"
          visible={isCancelModalVisible}
          onOk={handleCancelOrder}
          onCancel={() => {
            setIsCancelModalVisible(false);
            setCancellationReason("");
          }}
          okText="Submit"
          cancelText="Close"
        >
          <p>Please provide a reason for cancelling this order:</p>
          <Input.TextArea
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            rows={4}
          />
        </Modal>

        {/* Status Change Modal */}
        <Modal
          title="Order Status Change"
          visible={isStatusChangeModalVisible}
          footer={null}
          onCancel={() => {
            setIsStatusChangeModalVisible(false);
            setCurrentStatus(null);
          }}
          width={800}
        >
          <Form layout="vertical">
            <Form.Item label="New Status">
              <Select
                placeholder="Select status"
                value={currentStatus}
                onChange={setCurrentStatus}
              >
                <Select.Option value="accession">Accession</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button type="primary" onClick={handleStatusChange}>
                Submit
              </Button>
              <Button
                className="ml-4"
                onClick={() => {
                  setIsStatusChangeModalVisible(false);
                  setCurrentStatus(null);
                }}
              >
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Spin>
  );
};

export default Order;